/**
 * Recurring Invoices — list + inline editor for templates that the daily cron
 * materialises into real invoices.
 */
import { useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import {
  Plus, Repeat, Trash2, Pencil, Play, Loader2, X, CheckCircle2, Calendar,
  ToggleLeft, ToggleRight, FileText, AlertTriangle, ChevronRight, ChevronDown,
} from "lucide-react";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";

const FREQ_LABEL = {
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

export default function RecurringInvoices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {} for new, template for edit
  const [previewFor, setPreviewFor] = useState(null); // {id, runs}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/recurring-invoices");
      setRows(r.data);
    } catch { toast.error("Failed to load recurring invoices"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePreview = async (row) => {
    if (previewFor?.id === row.id) { setPreviewFor(null); return; }
    try {
      const r = await api.get(`/recurring-invoices/${row.id}/preview?count=6`);
      setPreviewFor({ id: row.id, runs: r.data.runs });
    } catch { toast.error("Preview failed"); }
  };

  const total = (lines, disc, tax) => {
    const sub = (lines || []).reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
    const d = sub * (Number(disc) || 0) / 100;
    const t = (sub - d) * (Number(tax) || 0) / 100;
    return sub - d + t;
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/recurring-invoices/${row.id}`, { active: !row.active });
      toast.success(row.active ? "Paused" : "Resumed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const runNow = async (row) => {
    try {
      const r = await api.post(`/recurring-invoices/${row.id}/run-now`);
      toast.success(`Created invoice ${r.data.invoice_number}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Run failed"); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete the "${row.name}" template? This stops future auto-generation; existing invoices stay put.`)) return;
    try {
      await api.delete(`/recurring-invoices/${row.id}`);
      toast.success("Template removed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <>
      <PageHeader
        title="Recurring Invoices"
        subtitle="Retainers, monthly fees, and anything you bill on autopilot"
        testid="recurring-header"
        actions={
          <button
            onClick={() => setEditing({})}
            data-testid="recurring-new-btn"
            className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 flex items-center gap-1.5"
          >
            <Plus size={14} /> New template
          </button>
        }
      />
      <PageBody>
        <div className="border bg-white" style={{ borderColor: "var(--ll-border)" }}>
          {loading ? (
            <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]">
              <Loader2 className="mx-auto animate-spin mb-2" size={16} /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center" data-testid="recurring-empty">
              <Repeat size={32} className="mx-auto mb-3 opacity-40" />
              <div className="font-display font-bold mb-1">No recurring templates yet</div>
              <div className="text-sm text-[var(--ll-ink-soft)] mb-4">
                Set up a template once and we'll create a fresh invoice on the cadence you choose.
              </div>
              <button
                onClick={() => setEditing({})}
                data-testid="recurring-empty-new"
                className="text-sm px-3 py-2 bg-[#0b5d9e] text-white inline-flex items-center gap-1.5"
              >
                <Plus size={14} /> New template
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#0b5d9e] text-white">
                <tr className="text-[10px] uppercase tracking-widest">
                  <th className="px-4 py-2.5 text-left">Status</th>
                  <th className="px-4 py-2.5 text-left">Name</th>
                  <th className="px-4 py-2.5 text-left">Bill To</th>
                  <th className="px-4 py-2.5 text-left">Cadence</th>
                  <th className="px-4 py-2.5 text-right">Total</th>
                  <th className="px-4 py-2.5 text-left">Next run</th>
                  <th className="px-4 py-2.5 text-right">Runs</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Fragment key={row.id}>
                  <tr className="border-t" data-testid={`recurring-row-${row.id}`} style={{ borderColor: "var(--ll-border)" }}>
                    <td className="px-4 py-2.5">
                      <span
                        className="px-2 py-0.5 text-[10px] uppercase tracking-widest"
                        style={{
                          background: row.active ? "#DCFCE7" : "#F3F4F6",
                          color: row.active ? "#166534" : "#6B7280",
                        }}
                      >
                        {row.active ? "Active" : "Paused"}
                      </span>
                      {row.auto_send && (
                        <span className="ml-1.5 text-[10px] uppercase tracking-widest" style={{ background: "#DBEAFE", color: "#1E40AF", padding: "2px 6px" }}>
                          Auto-send
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-medium">{row.name}</td>
                    <td className="px-4 py-2.5 truncate max-w-[200px]" title={row.bill_to_name}>{row.bill_to_name}</td>
                    <td className="px-4 py-2.5">
                      {FREQ_LABEL[row.frequency] || row.frequency}
                      {row.interval > 1 && <span className="text-xs text-[var(--ll-ink-soft)]"> · every {row.interval}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-fin">
                      {fmtMoney(total(row.lines, row.discount_percent, row.tax_percent))}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {row.next_run_date ? (
                        <button
                          type="button"
                          onClick={() => togglePreview(row)}
                          data-testid={`recurring-preview-toggle-${row.id}`}
                          className="inline-flex items-center gap-1 hover:underline"
                          title="Click to see the next 6 run dates"
                        >
                          {previewFor?.id === row.id ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                          <Calendar size={11} className="opacity-60" />
                          {fmtDate(row.next_run_date)}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs">
                      {row.runs_completed || 0}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn
                          title="Run now (materialise an invoice today)"
                          onClick={() => runNow(row)}
                          testid={`recurring-run-${row.id}`}
                        >
                          <Play size={13} />
                        </IconBtn>
                        <IconBtn
                          title={row.active ? "Pause" : "Resume"}
                          onClick={() => toggleActive(row)}
                          testid={`recurring-toggle-${row.id}`}
                        >
                          {row.active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        </IconBtn>
                        <IconBtn title="Edit" onClick={() => setEditing(row)} testid={`recurring-edit-${row.id}`}>
                          <Pencil size={13} />
                        </IconBtn>
                        <IconBtn title="Delete" onClick={() => remove(row)} testid={`recurring-delete-${row.id}`} danger>
                          <Trash2 size={13} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                  {previewFor?.id === row.id && (
                    <tr className="border-t bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }} data-testid={`recurring-preview-row-${row.id}`}>
                      <td colSpan={8} className="px-4 py-3">
                        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">
                          Next 6 scheduled runs
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {previewFor.runs.map((r) => (
                            <div
                              key={r.date}
                              data-testid={`recurring-preview-run-${row.id}-${r.date}`}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-xs"
                              style={{
                                background: r.is_weekend ? "#FEF3C7" : "white",
                                borderColor: r.is_weekend ? "#F59E0B" : "var(--ll-border)",
                                color: r.is_weekend ? "#92400E" : undefined,
                              }}
                              title={r.is_weekend ? "Weekend — invoice will be generated on the weekend" : ""}
                            >
                              {r.is_weekend && <AlertTriangle size={11} />}
                              <span className="font-mono-fin">{fmtDate(r.date)}</span>
                              <span className="text-[10px] opacity-70">· {r.weekday}</span>
                            </div>
                          ))}
                        </div>
                        {previewFor.runs.some((r) => r.is_weekend) && (
                          <div className="text-[11px] text-[#B45309] mt-2 flex items-center gap-1">
                            <AlertTriangle size={11} /> Some runs land on a weekend — consider adjusting the day of month.
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </PageBody>

      {editing !== null && (
        <TemplateEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

function IconBtn({ children, title, onClick, testid, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-1.5 border hover:bg-[var(--ll-mint)] ${danger ? "hover:bg-red-50 hover:text-red-600" : ""}`}
      style={{ borderColor: "var(--ll-border)" }}
    >
      {children}
    </button>
  );
}

/* ---------------- Editor ---------------- */

function emptyLine() {
  return { kind: "service", description: "", quantity: 1, unit_price: 0 };
}

function TemplateEditor({ initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    bill_to_name: initial?.bill_to_name || "",
    bill_to_email: initial?.bill_to_email || "",
    bill_to_address: initial?.bill_to_address || "",
    lines: initial?.lines?.length ? initial.lines : [emptyLine()],
    discount_percent: initial?.discount_percent || 0,
    tax_percent: initial?.tax_percent || 0,
    notes: initial?.notes || "",
    terms: initial?.terms || "",
    frequency: initial?.frequency || "monthly",
    interval: initial?.interval || 1,
    day_of_month: initial?.day_of_month || "",
    due_days: initial?.due_days || 30,
    auto_send: initial?.auto_send ?? false,
    start_date: initial?.start_date || new Date().toISOString().slice(0, 10),
    end_date: initial?.end_date || "",
    active: initial?.active ?? true,
  }));
  const [saving, setSaving] = useState(false);

  const totals = useMemo(() => {
    const sub = form.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
    const d = sub * (Number(form.discount_percent) || 0) / 100;
    const t = (sub - d) * (Number(form.tax_percent) || 0) / 100;
    return { sub, d, t, total: sub - d + t };
  }, [form]);

  const setLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, k) => k === i ? { ...l, ...patch } : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, k) => k !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Give it a name"); return; }
    if (!form.bill_to_name.trim()) { toast.error("Add a bill-to name"); return; }
    if (!form.lines.some((l) => l.description.trim())) { toast.error("Add at least one line"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        day_of_month: form.day_of_month ? Number(form.day_of_month) : null,
        end_date: form.end_date || null,
        interval: Number(form.interval) || 1,
        due_days: Number(form.due_days) || 30,
        discount_percent: Number(form.discount_percent) || 0,
        tax_percent: Number(form.tax_percent) || 0,
        lines: form.lines.filter((l) => l.description.trim()).map((l) => ({
          kind: l.kind, description: l.description, sku: l.sku || null,
          quantity: Number(l.quantity) || 0, unit_price: Number(l.unit_price) || 0,
        })),
      };
      if (isNew) await api.post("/recurring-invoices", payload);
      else       await api.patch(`/recurring-invoices/${initial.id}`, payload);
      toast.success(isNew ? "Template created" : "Template updated");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex justify-end" onClick={onClose} data-testid="recurring-editor">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white h-full w-full max-w-3xl shadow-xl overflow-y-auto">
        <div className="px-5 py-3 border-b bg-[#0b5d9e] text-white flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-75">{isNew ? "Create" : "Edit"}</div>
            <div className="font-display font-bold text-sm">{isNew ? "New recurring template" : form.name}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="recurring-editor-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <Field label="Template name *">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="recurring-name-input" placeholder="e.g. Monthly retainer — Acme Corp" />
          </Field>

          <div className="border p-4" style={{ borderColor: "var(--ll-border)" }}>
            <div className="text-xs font-display font-bold mb-2">Bill to</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name *">
                <input required value={form.bill_to_name} onChange={(e) => setForm({ ...form, bill_to_name: e.target.value })} className="input" data-testid="recurring-billto-name" />
              </Field>
              <Field label="Email (for auto-send)">
                <input type="email" value={form.bill_to_email} onChange={(e) => setForm({ ...form, bill_to_email: e.target.value })} className="input" data-testid="recurring-billto-email" />
              </Field>
              <Field label="Address" full>
                <textarea rows={2} value={form.bill_to_address} onChange={(e) => setForm({ ...form, bill_to_address: e.target.value })} className="input" />
              </Field>
            </div>
          </div>

          <div className="border p-4" style={{ borderColor: "var(--ll-border)" }}>
            <div className="text-xs font-display font-bold mb-2">Cadence</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Frequency">
                <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="input" data-testid="recurring-frequency">
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </select>
              </Field>
              <Field label="Every N">
                <input type="number" min="1" value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })} className="input" data-testid="recurring-interval" />
              </Field>
              {(form.frequency === "monthly" || form.frequency === "quarterly" || form.frequency === "annually") && (
                <Field label="Day of month (optional)">
                  <input type="number" min="1" max="31" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} className="input" placeholder="1-31" data-testid="recurring-day-of-month" />
                </Field>
              )}
              <Field label="Due in (days)">
                <input type="number" min="0" value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })} className="input" data-testid="recurring-due-days" />
              </Field>
              <Field label="Start date *">
                <input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input" data-testid="recurring-start-date" />
              </Field>
              <Field label="End date (optional)">
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input" data-testid="recurring-end-date" />
              </Field>
              <Field label="Status" full={false}>
                <label className="flex items-center gap-2 py-1.5 text-sm">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="recurring-active" />
                  Active (uncheck to pause)
                </label>
              </Field>
              <Field label="Delivery">
                <label className="flex items-center gap-2 py-1.5 text-sm">
                  <input type="checkbox" checked={form.auto_send} onChange={(e) => setForm({ ...form, auto_send: e.target.checked })} data-testid="recurring-auto-send" />
                  Auto-send email on creation
                </label>
              </Field>
            </div>
          </div>

          <div className="border" style={{ borderColor: "var(--ll-border)" }}>
            <div className="px-3 py-2 bg-[#0b5d9e] text-white text-[10px] uppercase tracking-widest flex items-center justify-between">
              <span>Line items</span>
              <button type="button" onClick={addLine} className="flex items-center gap-1 hover:underline" data-testid="recurring-add-line">
                <Plus size={11} /> Add
              </button>
            </div>
            <div className="p-3 space-y-2">
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`recurring-line-${i}`}>
                  <select value={l.kind} onChange={(e) => setLine(i, { kind: e.target.value })} className="input col-span-2">
                    <option value="service">Service</option>
                    <option value="product">Product</option>
                  </select>
                  <input placeholder="Description" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} className="input col-span-5" />
                  <input type="number" step="0.01" min="0" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="input col-span-2 text-right font-mono-fin" />
                  <input type="number" step="0.01" min="0" placeholder="Rate" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} className="input col-span-2 text-right font-mono-fin" />
                  <button type="button" onClick={() => rmLine(i)} className="col-span-1 p-2 border hover:bg-red-50 hover:text-red-600 justify-self-end" style={{ borderColor: "var(--ll-border)" }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-3">
              <Field label="Discount %">
                <input type="number" step="0.01" min="0" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="input" />
              </Field>
              <Field label="Tax %">
                <input type="number" step="0.01" min="0" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} className="input" />
              </Field>
            </div>
            <div className="border p-4 space-y-1" style={{ borderColor: "var(--ll-border)" }}>
              <TotalRow label="Subtotal" value={totals.sub} />
              {totals.d > 0 && <TotalRow label={`Discount (${form.discount_percent}%)`} value={-totals.d} />}
              {totals.t > 0 && <TotalRow label={`Tax (${form.tax_percent}%)`} value={totals.t} />}
              <div className="h-px my-2" style={{ background: "var(--ll-border)" }} />
              <TotalRow label="Total per run" value={totals.total} bold />
            </div>
          </div>

          <Field label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" />
          </Field>
          <Field label="Terms">
            <textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="input" />
          </Field>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex items-center justify-end gap-2" style={{ borderColor: "var(--ll-border)" }}>
          <button type="button" onClick={onClose} className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5" data-testid="recurring-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {isNew ? "Create template" : "Save changes"}
          </button>
        </div>

        <style>{`
          .input { width: 100%; border: 1px solid var(--ll-border); padding: 6px 8px; font-size: 13px; background: white; }
          .input:focus { outline: 2px solid #0b5d9e; outline-offset: -1px; }
        `}</style>
      </form>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">{label}</div>
      {children}
    </label>
  );
}

function TotalRow({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between py-1 ${bold ? "font-bold text-base" : "text-sm"}`}>
      <span className="text-[var(--ll-ink-soft)]">{label}</span>
      <span className="font-mono-fin">{fmtMoney(value)}</span>
    </div>
  );
}
